import axios from 'axios';
import { AssistantVariant, Message, PdfDocument } from '../models/models';
import { extractCitations, mapServerMessageToClientMessage } from '../utils/chat-utils';

export interface ConversationData {
  id: number;
  title: string;
  messages: Message[];
  answerVariants: AssistantVariant[];
  selectedGroupIds: number[];
  selectedDocIds: number[];
  selectedModelId: number | null;
  searchInternet: boolean;
  documents: PdfDocument[];
}

export interface ConversationDetailResponse {
  id: number;
  title: string;
  messages?: Message[];
  conversationCollections?: Array<{
    id: number;
    name: string;
    lastModificationDate: string;
    collectionType: string;
    amountOfDocuments: number;
    owner: any;
  }>;
  documentIds?: number[];
  lastAnswerVariants?: AssistantVariant[];
  selectedModelId?: number | null;
  searchInternet?: boolean;
  documents?: PdfDocument[];
}

export interface CreateConversationParams {
  selectedModelId?: number | null;
  selectedGroupIds?: number[];
  selectedDocIds?: number[];
  searchInternet?: boolean;
}

export interface UpdateSourcesParams {
  selectedGroupIds: number[];
  selectedDocIds: number[];
}

export class ConversationService {
  async fetchConversation(id: string): Promise<ConversationData> {
    const response = await axios.get<ConversationDetailResponse>(`/chat/conversation/${id}`);
    const data = response.data;


    const mappedMessages = (data.messages || []).map(mapServerMessageToClientMessage);

    return {
      id: data.id ?? parseInt(id, 10),
      title: data.title ?? '',
      messages: mappedMessages,
      answerVariants: (data.lastAnswerVariants || []).map((v: AssistantVariant) => {
        const { cleanText, citations } = extractCitations(v.content);
        return {
          id: v.id,
          timestamp: v.timestamp,
          content: cleanText,
          active: v.active,
          documents: v.documents || [],
          citations: citations.length > 0 ? citations : undefined
        };
      }),
      selectedGroupIds: (data.conversationCollections || []).map(c => c.id),
      selectedDocIds: data.documentIds || [],
      selectedModelId: data.selectedModelId || null,
      searchInternet: data.searchInternet || false,
      documents: data.documents || [],
    };
  }

  async createConversation(params: CreateConversationParams = {}): Promise<string> {
    const response = await axios.post<{ id: string }>('/chat/conversation');
    return response.data.id;
  }

  async updateSources(conversationId: string, params: UpdateSourcesParams): Promise<void> {
    if (params.selectedGroupIds.length > 0) {
      await axios.patch(`/chat/conversation/${conversationId}/collections`,
        params.selectedGroupIds
      );
    }

    if (params.selectedDocIds.length > 0) {
      await axios.patch(`/chat/conversation/${conversationId}/documents`, {
        documentIds: params.selectedDocIds
      });
    }
  }

  async updateModel(conversationId: string, modelId: number): Promise<void> {
    await axios.patch(`/models/conversation/${conversationId}`, {
      modelId: modelId,
    });
  }

  async activateVariant(variantId: number): Promise<void> {
    await axios.patch(`/chat/answer/${variantId}/activate`);
  }

  async fetchDocuments(conversationId: string): Promise<PdfDocument[]> {
    const response = await axios.get<PdfDocument[]>(`/chat/conversation/${conversationId}/documents`);
    return response.data;
  }

  async exportToPdf(conversationId: string): Promise<Blob> {
    const response = await axios.get(`/chat/conversation/${conversationId}/export`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async cancelGeneration(conversationId: number): Promise<void> {
    await axios.post(`/chat/cancel/${conversationId}`);
  }
}

export const conversationService = new ConversationService();
